import {createFragmentContainer, graphql} from 'react-relay';
import {decompressData} from '../util/DataDecompressUtil';
import {getCurrency} from '../util/BudgetUtils';
import {useState} from 'react';
import {buildTimeAndMaterialsRow, buildFixedPriceDeliverablesRow} from '../util/DeliverableRevenueRecognitionUtil';

// Total Time/Total Expenses render directly under the total, ahead of Time & Materials/Fixed
// Price (Deliverables). Exported so the row order is asserted by a test rather than only being
// implied by call-site order.
export function buildInsertedRows({
	hasExpenses,
	hasDeliverables,
	totalTimeRow,
	totalExpensesRow,
	timeAndMaterials,
	fixedPriceDeliverables,
}) {
	return [
		...(hasExpenses ? [totalTimeRow, totalExpensesRow] : []),
		...(hasDeliverables ? [timeAndMaterials, fixedPriceDeliverables] : []),
	];
}

const RevenueRecognitionLoader = ({
	viewer,
	aggregateLevel,
	aggregates,
	expenseAggregates,
	children,
	retry,
	timestamp,
	hasExpenses,
	hasDeliverables,
	pricedDeliverableIds,
}) => {
	const [financialQueryTimestamp, setFinancialQueryTimestamp] = useState(timestamp);
	if (timestamp !== financialQueryTimestamp) {
		setFinancialQueryTimestamp(timestamp);
		retry();
	}
	const {
		aggregatedFinancialNumbers,
		financialNumbers,
		workAggregatedFinancialNumbers,
		workFinancialNumbers,
		expenseAggregatedFinancialNumbers,
		expenseFinancialNumbers,
		timeAndMaterialsTimeBreakdown,
		timeAndMaterialsExpensesBreakdown,
		fixedPriceDeliverablesTotalTimeBreakdown,
		fixedPriceDeliverablesTotalExpensesBreakdown,
		fixedPriceDeliverablesTimeBreakdown,
		fixedPriceDeliverablesExpensesBreakdown,
	} = viewer.project;
	const currency = getCurrency(viewer.company, viewer.project);
	const processedData = decompressData(
		aggregatedFinancialNumbers,
		financialNumbers,
		currency,
		aggregateLevel,
		aggregates,
		false
	);
	const rows = [...processedData.rows];

	const recognitionAmountIndex = rows.findIndex(row => row.aggregate === 'recognitionAmount');
	rows[recognitionAmountIndex].aggregate = 'recognitionTotal';
	rows.splice(recognitionAmountIndex + 1, 0, {dataArray: []});

	let totalTimeDataArray = null;
	let totalTimeRow = null;
	let totalExpensesRow = null;

	if (hasExpenses) {
		const processedWorkData = decompressData(
			workAggregatedFinancialNumbers,
			workFinancialNumbers,
			currency,
			aggregateLevel,
			aggregates,
			false
		);
		const processedExpenseData = decompressData(
			expenseAggregatedFinancialNumbers,
			expenseFinancialNumbers,
			currency,
			aggregateLevel,
			expenseAggregates,
			false
		);

		totalTimeRow = processedWorkData.rows[0];
		totalTimeRow.aggregate = 'totalTime';
		totalTimeDataArray = totalTimeRow.dataArray;

		totalExpensesRow = processedExpenseData.rows.find(row => row.aggregate === 'billableTotalTimeAndExpensesAtCompletion');
		totalExpensesRow.aggregate = 'totalExpenses';
	}

	let timeAndMaterials = null;
	let fixedPriceDeliverables = null;

	if (hasDeliverables) {
		timeAndMaterials = buildTimeAndMaterialsRow({
			columns: processedData.columns,
			timeRows: timeAndMaterialsTimeBreakdown,
			expenseRows: timeAndMaterialsExpensesBreakdown,
			currency,
		});
		fixedPriceDeliverables = buildFixedPriceDeliverablesRow({
			columns: processedData.columns,
			totalTimeRows: fixedPriceDeliverablesTotalTimeBreakdown,
			totalExpenseRows: fixedPriceDeliverablesTotalExpensesBreakdown,
			perDeliverableTimeRows: fixedPriceDeliverablesTimeBreakdown,
			perDeliverableExpenseRows: fixedPriceDeliverablesExpensesBreakdown,
			allDeliverableIds: pricedDeliverableIds,
			currency,
		});
	}

	const insertedRows = buildInsertedRows({
		hasExpenses,
		hasDeliverables,
		totalTimeRow,
		totalExpensesRow,
		timeAndMaterials,
		fixedPriceDeliverables,
	});
	rows.splice(recognitionAmountIndex + 1, 0, ...insertedRows);

	const result = {
		data: {
			rows: rows,
			columns: processedData.columns,
			totalTimeDataArray,
		},
	};

	return children(result);
};

export const revenueRecognitionLoaderQuery = graphql`
	query RevenueRecognitionLoader_Query(
		$projectId: ID!
		$startYear: Int
		$startMonth: Int
		$startDay: Int
		$endYear: Int
		$endMonth: Int
		$endDay: Int
		$aggregateLevel: AggregateLevel!
		$aggregates: [FinancialAggregatedNumber]!
		$searchQuery: TaskSearchQueryType
		$expenseAggregates: [FinancialAggregatedNumber]!
		$expenseSearchQuery: TaskSearchQueryType
		$hasExpenses: Boolean!
		$hasDeliverables: Boolean!
	) {
		viewer {
			actualPersonId
			component(name: "revenue_recognition_loader")
			...RevenueRecognitionLoader_viewer
				@arguments(
					projectId: $projectId
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					aggregateLevel: $aggregateLevel
					aggregates: $aggregates
					searchQuery: $searchQuery
					expenseAggregates: $expenseAggregates
					expenseSearchQuery: $expenseSearchQuery
					hasExpenses: $hasExpenses
					hasDeliverables: $hasDeliverables
				)
		}
	}
`;

export default createFragmentContainer(RevenueRecognitionLoader, {
	viewer: graphql`
		fragment RevenueRecognitionLoader_viewer on Viewer
		@argumentDefinitions(
			projectId: {type: "ID!"}
			startYear: {type: "Int"}
			startMonth: {type: "Int"}
			startDay: {type: "Int"}
			endYear: {type: "Int"}
			endMonth: {type: "Int"}
			endDay: {type: "Int"}
			aggregateLevel: {type: "AggregateLevel!"}
			aggregates: {type: "[FinancialAggregatedNumber]!"}
			searchQuery: {type: "TaskSearchQueryType"}
			expenseAggregates: {type: "[FinancialAggregatedNumber]!"}
			expenseSearchQuery: {type: "TaskSearchQueryType"}
			hasExpenses: {type: "Boolean!"}
			hasDeliverables: {type: "Boolean!"}
		) {
			id
			company {
				currency
			}
			project(internalId: $projectId) {
				rateCard {
					currency
				}
				aggregatedFinancialNumbers(
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					aggregateLevel: $aggregateLevel
					aggregates: $aggregates
					convertToProjectCurrency: true
					addAccumulatedNumbers: true
				)
				financialNumbers(
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					convertToProjectCurrency: true
				) {
					recognitionAmount
					estimatedCost
					recognitionProfit
					recognitionProfitPercentage
				}

				workAggregatedFinancialNumbers: aggregatedFinancialNumbers(
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					aggregateLevel: $aggregateLevel
					aggregates: $aggregates
					convertToProjectCurrency: true
					addAccumulatedNumbers: true
					searchQuery: $searchQuery
				) @include(if: $hasExpenses)
				workFinancialNumbers: financialNumbers(
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					convertToProjectCurrency: true
					searchQuery: $searchQuery
				) @include(if: $hasExpenses) {
					recognitionAmount
				}

				expenseAggregatedFinancialNumbers: aggregatedFinancialNumbers(
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					aggregateLevel: $aggregateLevel
					aggregates: $expenseAggregates
					convertToProjectCurrency: true
					addAccumulatedNumbers: true
					searchQuery: $expenseSearchQuery
				) @include(if: $hasExpenses)
				expenseFinancialNumbers: financialNumbers(
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					convertToProjectCurrency: true
					searchQuery: $expenseSearchQuery
				) @include(if: $hasExpenses) {
					billableTotalTimeAndExpensesAtCompletion
					allTotalTimeAndExpensesAtCompletion
				}

				timeAndMaterialsTimeBreakdown: deliverableRevenueRecognitionBreakdown(
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					convertToProjectCurrency: true
					searchQuery: $searchQuery
					groupByDeliverable: false
					deliverableIdFilterMode: NOT_IN
				) @include(if: $hasDeliverables) {
					year
					month
					financialNumbers {
						recognitionAmount
					}
				}
				timeAndMaterialsExpensesBreakdown: deliverableRevenueRecognitionBreakdown(
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					convertToProjectCurrency: true
					searchQuery: $expenseSearchQuery
					groupByDeliverable: false
					deliverableIdFilterMode: NOT_IN
				) @include(if: $hasDeliverables) {
					year
					month
					financialNumbers {
						billableTotalTimeAndExpensesAtCompletion
					}
				}
				fixedPriceDeliverablesTotalTimeBreakdown: deliverableRevenueRecognitionBreakdown(
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					convertToProjectCurrency: true
					searchQuery: $searchQuery
					groupByDeliverable: false
				) @include(if: $hasDeliverables) {
					year
					month
					financialNumbers {
						recognitionAmount
					}
				}
				fixedPriceDeliverablesTotalExpensesBreakdown: deliverableRevenueRecognitionBreakdown(
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					convertToProjectCurrency: true
					searchQuery: $expenseSearchQuery
					groupByDeliverable: false
				) @include(if: $hasDeliverables) {
					year
					month
					financialNumbers {
						billableTotalTimeAndExpensesAtCompletion
					}
				}
				fixedPriceDeliverablesTimeBreakdown: deliverableRevenueRecognitionBreakdown(
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					convertToProjectCurrency: true
					searchQuery: $searchQuery
				) @include(if: $hasDeliverables) {
					deliverableId
					year
					month
					financialNumbers {
						recognitionAmount
					}
				}
				fixedPriceDeliverablesExpensesBreakdown: deliverableRevenueRecognitionBreakdown(
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					convertToProjectCurrency: true
					searchQuery: $expenseSearchQuery
				) @include(if: $hasDeliverables) {
					deliverableId
					year
					month
					financialNumbers {
						billableTotalTimeAndExpensesAtCompletion
					}
				}
			}
		}
	`,
});
